import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';
import { OidcUserStatus, useOidc, useOidcUser } from '@axa-fr/react-oidc';
import LoadingModal from '../modal/LoadingModal';

/**
 * Component to protect routes that require authentication
 */
const AuthenticatedRoute = ({ children }) => {
  const location = useLocation();
  const { isAuthenticated, login } = useOidc();
  const { oidcUserLoadingState } = useOidcUser();
  const [forceLogout, setForceLogout] = useState(false);
  const loginStartedRef = useRef(false);
  const navigate = useNavigate();

  useEffect(() => {
    const bc = new BroadcastChannel('logout_broadcast');

    bc.onmessage = () => {
      // Clear personId in other tabs when logout is broadcast so readers don't
      // pick up a stale personId after a logout in a different tab.
      try {
        sessionStorage.removeItem('personId');
      } catch (e) {
        // ignore
      }
      setForceLogout(true);
    };

    // Cleanup
    return () => {
      // prevent memory leak
      bc.close();
    };
  }, []);

  useEffect(() => {
    if (forceLogout && isAuthenticated) {
      navigate('/logout');
    }
  }, [forceLogout, isAuthenticated, navigate]);

  // Login redirect - must be called before any conditional returns
  useEffect(() => {
    const isCallbackRoute = location.pathname.includes('/authdepot/callback')
      || location.pathname.includes('/oidc/callback');
    const shouldLogin = !isAuthenticated
      || oidcUserLoadingState === OidcUserStatus.LoadingError;

    if (shouldLogin
      && oidcUserLoadingState !== OidcUserStatus.Loading
      && !isCallbackRoute
      && !loginStartedRef.current) {
      loginStartedRef.current = true;
      const currentPath = location.pathname + (location.search || '');
      sessionStorage.setItem('returnUrl', currentPath);
      login();
    }
  }, [
    isAuthenticated,
    oidcUserLoadingState,
    location.pathname,
    location.search,
    login,
  ]);

  // Debug output
  // console.log('AuthenticatedRoute status:', {
  //   isAuthenticated, oidcUserLoadingState, path: location.pathname,
  // });

  // handle callback URLs to avoid redirect loop
  if (location.pathname.includes('/authdepot/callback')
    || location.pathname.includes('/oidc/callback')) {
    return children;
  }

  if (oidcUserLoadingState === OidcUserStatus.Loading) {
    // eslint-disable-next-line react/jsx-filename-extension
    return <LoadingModal />;
  }

  if (isAuthenticated && oidcUserLoadingState !== OidcUserStatus.LoadingError) {
    return children;
  }

  // eslint-disable-next-line react/jsx-filename-extension
  return <LoadingModal />;
};

AuthenticatedRoute.propTypes = {
  children: PropTypes.node.isRequired,
};

export default AuthenticatedRoute;
